/**
 * BLOC 11/10 11h45 + 12h20 — UI pour gérer les numéros supplémentaires d'un
 * contact ou d'un lead (CardDAV identification des appels iPhone).
 *
 * Props :
 *  - value: Array<{e164, type, label?}>  (depuis payload API)
 *  - onChange: (nextArray) => void       (appelé à chaque modification)
 *  - disabled?: boolean
 *
 * Mobile-first 390px : zones tactiles ≥44px, inputs avec `inputMode="tel"`.
 * Validation E.164 côté backend (Pydantic + libphonenumber) ; côté UI on se
 * contente d'afficher les erreurs 422 renvoyées par l'API.
 */
import { useRef, useState } from "react";
import { Plus, Trash2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY_ENTRY = { e164: "", type: "mobile", label: "" };

export function PhonesManager({ value, onChange, disabled = false }) {
  const list = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState(EMPTY_ENTRY);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const addEntry = async () => {
    if (!draft.e164.trim() || busy) return;
    const candidate = { ...draft, e164: draft.e164.trim(), label: draft.label.trim() || null };
    const next = [...list, candidate];
    setBusy(true);
    try {
      // onChange est async : attendre réponse serveur. Si rejet 422 → ne vide pas la saisie.
      const result = onChange(next);
      if (result && typeof result.then === "function") {
        await result;
      }
      // BLOC 11/10 14h10 — vider UNIQUEMENT après succès serveur
      setDraft(EMPTY_ENTRY);
    } catch (err) {
      // BLOC 11/10 14h10 — Fix 2 : saisie préservée + toast FR actionnable
      const status = err?.response?.status || err?.status;
      if (status === 422) {
        toast.error("Numéro invalide : vérifiez le format");
      } else {
        toast.error("Impossible d'ajouter le numéro. Réessayez.");
      }
      // Restaurer le focus sur l'input e164
      setTimeout(() => inputRef.current?.focus(), 50);
    } finally {
      setBusy(false);
    }
  };

  const removeAt = async (i) => {
    const next = list.filter((_, idx) => idx !== i);
    try {
      const result = onChange(next);
      if (result && typeof result.then === "function") {
        await result;
      }
    } catch (err) {
      toast.error("Impossible de supprimer ce numéro. Réessayez.");
    }
  };

  return (
    <div className="space-y-3" data-testid="phones-manager">
      {list.length === 0 && (
        <div className="text-xs text-slate-500" data-testid="phones-empty">
          Aucun numéro supplémentaire. Le téléphone principal reste utilisé pour les devis, factures et SMS.
        </div>
      )}
      {list.map((p, i) => (
        <div
          key={`${p.e164}-${i}`}
          className="flex items-center gap-2 rounded border border-slate-800 bg-slate-950/50 p-2"
          data-testid={`phone-entry-${i}`}
        >
          <Phone className="h-4 w-4 text-sky-400 shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-mono text-slate-100 truncate" data-testid={`phone-e164-${i}`}>
              {p.e164}
            </div>
            <div className="text-xs text-slate-400">
              {p.type}
              {p.label ? ` · ${p.label}` : ""}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeAt(i)}
            disabled={disabled}
            data-testid={`phone-remove-btn-${i}`}
            className="min-h-11 min-w-11 text-rose-400 hover:text-rose-300"
            aria-label="Supprimer ce numéro"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {/* Ajout d'un nouveau numéro */}
      <div className="rounded border border-dashed border-slate-700 bg-slate-950/30 p-3 space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Ajouter un numéro
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            ref={inputRef}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+32 470 12 34 56"
            value={draft.e164}
            onChange={(e) => setDraft({ ...draft, e164: e.target.value })}
            disabled={disabled || busy}
            className="flex-1 min-h-11"
            data-testid="phone-input-e164"
          />
          <Select
            value={draft.type}
            onValueChange={(v) => setDraft({ ...draft, type: v })}
            disabled={disabled}
          >
            <SelectTrigger className="sm:w-32 min-h-11" data-testid="phone-input-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mobile" data-testid="phone-type-mobile">Mobile</SelectItem>
              <SelectItem value="fixe" data-testid="phone-type-fixe">Fixe</SelectItem>
              <SelectItem value="autre" data-testid="phone-type-autre">Autre</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input
          type="text"
          placeholder="Libellé (optionnel) — ex. Bureau, Chantier"
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          disabled={disabled}
          className="min-h-11"
          data-testid="phone-input-label"
          maxLength={40}
        />
        <Button
          type="button"
          onClick={addEntry}
          disabled={disabled || busy || !draft.e164.trim()}
          size="sm"
          className="min-h-11 w-full sm:w-auto"
          data-testid="phone-add-btn"
        >
          <Plus className="mr-2 h-4 w-4" /> {busy ? "Ajout…" : "Ajouter"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Toggle particulier / société — pilote le mapping iOS CELL/HOME/WORK au rendu
 * vCard. Pour les sociétés, les fixes seront affichés comme "Travail" sur iOS.
 */
export function KindToggle({ value, onChange, disabled = false }) {
  const current = value || "particulier";
  return (
    <div
      className="inline-flex rounded-lg border border-slate-700 bg-slate-950/50 p-1"
      role="radiogroup"
      aria-label="Type de contact"
      data-testid="kind-toggle"
    >
      {["particulier", "societe"].map((k) => (
        <button
          key={k}
          type="button"
          role="radio"
          aria-checked={current === k}
          onClick={() => onChange(k)}
          disabled={disabled}
          className={`min-h-11 px-4 text-sm rounded-md transition-colors ${
            current === k
              ? "bg-sky-500 text-white shadow"
              : "text-slate-400 hover:text-slate-200"
          }`}
          data-testid={`kind-toggle-${k}`}
        >
          {k === "particulier" ? "Particulier" : "Société"}
        </button>
      ))}
    </div>
  );
}
